import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  Terminal,
  Info,
  MoreVertical,
  Play,
  Square,
  RefreshCw,
  Share2,
  CopyPlus,
  Trash2,
  Loader2,
  FolderOpen,
  Check,
  Pencil,
  Globe,
  UserMinus,
} from 'lucide-react'
import { type App, type Folder } from '../lib/api'
import { withMountBase } from '../lib/mountBase'
import { FOLDER_COLORS } from './folders/folderColors'
import { InlineDropdownFolderCreate } from './folders/AppContextMenu'
import AppDeleteDialog from './AppDeleteDialog'
type Permission = 'owner' | 'admin' | 'edit' | 'view'

interface AppActionsProps {
  app: App
  permission?: Permission
  onStart?: () => Promise<void>
  onStop?: () => Promise<void>
  onRebuild?: () => Promise<void>
  onRename?: () => void
  onClone?: () => void
  onShare?: () => void
  onPublish?: () => void
  onDelete?: () => Promise<void>
  onUnsubscribe?: () => void
  size?: 'sm' | 'md' | 'lg'
  showLabels?: boolean
  showDetails?: boolean
  isPublishing?: boolean
  // Folder support
  folders?: Folder[]
  appFolderIds?: number[]
  onMoveToFolder?: (folderId: number) => void
  onRemoveFromFolder?: (folderId: number) => void
  onCreateFolderAndMove?: (name: string) => void
}

export default function AppActions({
  app,
  permission = 'owner',
  onStart,
  onStop,
  onRebuild,
  onRename,
  onClone,
  onShare,
  onPublish,
  onDelete,
  onUnsubscribe,
  size = 'md',
  showLabels = false,
  showDetails = true,
  isPublishing = false,
  folders,
  appFolderIds,
  onMoveToFolder,
  onRemoveFromFolder,
  onCreateFolderAndMove,
}: AppActionsProps) {
  const navigate = useNavigate()
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [shareCopied, setShareCopied] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const canEdit = permission === 'owner' || permission === 'admin' || permission === 'edit'
  const canDelete = permission === 'owner'
  const hasFolders = folders && folders.length > 0

  const appFolder = `/home/coder/workspace/main`
  const workspacePath = withMountBase('/workspace', window.location.pathname)
  const appPath = withMountBase(`/a/${app.name}/`, window.location.pathname)
  const appUrl = `${window.location.origin}${appPath}`

  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-2.5',
  }

  const iconSizes = {
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  }

  async function handleAction(action: () => Promise<void>, name: string) {
    setActionLoading(name)
    try {
      await action()
    } finally {
      setActionLoading(null)
    }
  }

  const itemClass = 'w-full px-4 py-2 text-left text-sm hover:bg-secondary flex items-center gap-2 outline-none cursor-pointer'

  return (
    <div className="flex items-center gap-1.5">
      {/* Workspace - Primary action, emphasized (edit+ only) */}
      {canEdit && (
        
          <a title="Open the development workspace with terminal, file browser, and live preview"
            href={`${workspacePath}?app=${encodeURIComponent(app.name)}&folder=${encodeURIComponent(appFolder)}&url=${encodeURIComponent(appUrl)}`}
            className={`${sizeClasses[size]} rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex items-center gap-2`}
            onClick={(e) => e.stopPropagation()}
          >
            <Terminal className={iconSizes[size]} />
            {showLabels && <span className="text-sm font-medium">Workspace</span>}
          </a>
        
      )}

      {/* Details page (all permission levels) */}
      {showDetails && (
        <button title="View app details, settings, and sharing options"
          className={`${sizeClasses[size]} rounded-lg bg-secondary hover:bg-secondary/80 transition-colors relative`}
          onClick={(e) => { e.stopPropagation(); navigate(`/apps/${app.name}`) }}
        >
          <Info className={iconSizes[size]} />
          {app.has_pending_request && (
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-orange-500 rounded-full" />
          )}
        </button>
      )}

      {/* More menu - Radix DropdownMenu */}
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className={`${sizeClasses[size]} rounded-lg hover:bg-secondary transition-colors`}
            aria-label="More actions"
            title="More actions"
          >
            <MoreVertical className={iconSizes[size]} />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="w-48 bg-card border border-border rounded-lg shadow-lg z-50 py-1"
            align="end"
            sideOffset={4}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Start/Stop (edit+ only) */}
            {canEdit && (
              app.status === 'stopped' || app.status === 'error' ? (
                onStart && (
                  <DropdownMenu.Item
                    className={`${itemClass} text-green-500 disabled:opacity-50`}
                    disabled={actionLoading !== null}
                    onSelect={() => handleAction(onStart, 'start')}
                  >
                    {actionLoading === 'start' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    Start App
                  </DropdownMenu.Item>
                )
              ) : (
                onStop && (
                  <DropdownMenu.Item
                    className={`${itemClass} disabled:opacity-50`}
                    disabled={actionLoading !== null}
                    onSelect={() => handleAction(onStop, 'stop')}
                  >
                    {actionLoading === 'stop' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    Stop App
                  </DropdownMenu.Item>
                )
              )
            )}

            {/* Rebuild (edit+ only) */}
            {canEdit && onRebuild && (
              <DropdownMenu.Item
                className={`${itemClass} text-orange-500 disabled:opacity-50`}
                disabled={actionLoading !== null}
                onSelect={() => handleAction(onRebuild, 'rebuild')}
              >
                {actionLoading === 'rebuild' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Rebuild
              </DropdownMenu.Item>
            )}

            {/* Publish (edit+ only) */}
            {canEdit && onPublish && (
              <DropdownMenu.Item
                className={`${itemClass} text-purple-500 disabled:opacity-50`}
                disabled={isPublishing || actionLoading !== null}
                onSelect={(e) => {
                  if (isPublishing) {
                    e.preventDefault()
                    return
                  }
                  onPublish()
                }}
              >
                {isPublishing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Globe className="w-4 h-4" />
                )}
                {isPublishing ? 'Publishing…' : 'Publish'}
              </DropdownMenu.Item>
            )}

            <DropdownMenu.Separator className="h-px bg-border my-1" />

            {/* Edit Info (edit+ only) */}
            {canEdit && onRename && (
              <DropdownMenu.Item
                className={itemClass}
                onSelect={onRename}
              >
                <Pencil className="w-4 h-4" />
                Edit Info
              </DropdownMenu.Item>
            )}

            {/* Clone (edit+ only) */}
            {canEdit && onClone && (
              <DropdownMenu.Item
                className={itemClass}
                onSelect={onClone}
              >
                <CopyPlus className="w-4 h-4" />
                Clone
              </DropdownMenu.Item>
            )}

            {/* Share (all permission levels) */}
            <DropdownMenu.Item
              className={itemClass}
              onSelect={(e) => {
                if (canEdit && onShare) {
                  onShare()
                } else {
                  e.preventDefault() // Keep menu open to show feedback
                  navigator.clipboard.writeText(app.url)
                  setShareCopied(true)
                  setTimeout(() => setShareCopied(false), 1500)
                }
              }}
            >
              <Share2 className="w-4 h-4" />
              {shareCopied ? 'URL Copied!' : 'Share'}
            </DropdownMenu.Item>

            {/* Move to folder submenu */}
            {(hasFolders || onCreateFolderAndMove) && (
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger className={`${itemClass} data-[state=open]:bg-secondary`}>
                  <FolderOpen className="w-4 h-4" />
                  Move to Folder
                </DropdownMenu.SubTrigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent
                    className="bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px] z-50 max-h-[240px] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {onCreateFolderAndMove && (
                      <>
                        <InlineDropdownFolderCreate onCreateFolder={onCreateFolderAndMove} />
                        {hasFolders && <DropdownMenu.Separator className="h-px bg-border my-1" />}
                      </>
                    )}
                    {folders?.map((folder) => {
                      const colorSet = FOLDER_COLORS[folder.color] || FOLDER_COLORS.gray
                      const isInFolder = appFolderIds?.includes(folder.id)
                      return (
                        <DropdownMenu.CheckboxItem
                          key={folder.id}
                          checked={isInFolder}
                          className="px-3 py-1.5 text-sm hover:bg-secondary cursor-pointer flex items-center gap-2 outline-none"
                          onSelect={(e) => {
                            e.preventDefault()
                            if (isInFolder) {
                              onRemoveFromFolder?.(folder.id)
                            } else {
                              onMoveToFolder?.(folder.id)
                            }
                          }}
                        >
                          <DropdownMenu.ItemIndicator className="w-4 h-4 flex items-center justify-center shrink-0">
                            <Check className="w-3.5 h-3.5" />
                          </DropdownMenu.ItemIndicator>
                          <span className={`w-2.5 h-2.5 rounded-full ${colorSet.dot} shrink-0 ${isInFolder ? '' : 'ml-4'}`} />
                          {folder.name}
                        </DropdownMenu.CheckboxItem>
                      )
                    })}
                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>
            )}

            {/* Delete (owner only) */}
            {canDelete && onDelete && (
              <>
                <DropdownMenu.Separator className="h-px bg-border my-1" />
                <DropdownMenu.Item
                  className={`${itemClass} text-destructive`}
                  onSelect={() => setDeleteDialogOpen(true)}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </DropdownMenu.Item>
              </>
            )}

            {/* Unsubscribe (non-owner only — recipient-initiated, SHARE-01) */}
            {!canDelete && onUnsubscribe && (
              <>
                <DropdownMenu.Separator className="h-px bg-border my-1" />
                <DropdownMenu.Item
                  className={`${itemClass} text-destructive`}
                  onSelect={onUnsubscribe}
                >
                  <UserMinus className="w-4 h-4" />
                  Unsubscribe
                </DropdownMenu.Item>
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      {/* Delete confirmation dialog */}
      {canDelete && onDelete && (
        <AppDeleteDialog
          app={app}
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onDelete={onDelete}
        />
      )}
    </div>
  )
}
